import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Inventory2Outlined } from '@mui/icons-material';
import { Weight, normalizeInventoryDonationFoodType } from 'careit';
import { useTranslation } from 'react-i18next';
import { getS3Url, useFoodTypes } from 'src/constants';
import useLocaleUnit from 'src/hooks/useLocaleUnit';

export const parseInventoryImages = (images) => {
  if (!images) return [];

  let parsed = images;
  if (typeof images === 'string') {
    try {
      parsed = JSON.parse(images);
    } catch (_) {
      return [];
    }
  }

  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((image) => {
      if (typeof image === 'string') return image;
      return image?.image ?? image?.url ?? image?.file?.path ?? null;
    })
    .filter(Boolean);
};

const normalizeImage = (image) => {
  if (typeof image === 'string') return image;
  return image?.image ?? image?.url ?? image?.file?.path ?? null;
};

const getItemImage = (item) =>
  normalizeImage(item.primary_image) || parseInventoryImages(item.images)[0];

const getImageUrl = (image) =>
  /^(?:https?:|blob:|data:)/i.test(image || '') ? image : getS3Url(image);

function InventoryItemCard({
  item,
  onToggle,
  selected,
  weightUnit,
  preventIncrease = false,
}) {
  const { t } = useTranslation('translation');
  const foodTypes = useFoodTypes();
  const image = getItemImage(item);
  const normalizedFoodType = normalizeInventoryDonationFoodType(item.food_type);
  const category = foodTypes.find(
    (foodType) =>
      normalizeInventoryDonationFoodType(foodType.value) === normalizedFoodType
  );
  const storedWeight = Number(item.weight_per_item);
  const sourceWeightUnit = item.weight_per_item_unit || 'lbs';
  let displayWeight = null;

  if (Number.isFinite(storedWeight) && storedWeight >= 0) {
    try {
      displayWeight = new Weight(storedWeight, sourceWeightUnit).asRounded(
        weightUnit
      );
    } catch (_) {
      displayWeight = storedWeight;
    }
  }

  const remaining = Number(item.quantity_on_hand ?? 0);
  const outOfStock = remaining <= 0;
  const cannotSelect = preventIncrease && outOfStock && !selected;
  const chips = [
    category?.title || item.food_type
      ? {
          key: 'food-type',
          label: category?.title || item.food_type,
        }
      : null,
    displayWeight === null
      ? null
      : {
          key: 'weight',
          label: `${displayWeight} ${weightUnit}`,
        },
    {
      key: 'remaining',
      label: t('donations.inventory-source.remaining', { count: remaining }),
      color: outOfStock ? 'error' : 'default',
      variant: outOfStock ? 'filled' : 'outlined',
    },
  ].filter(Boolean);

  return (
    <ButtonBase
      component="button"
      type="button"
      aria-pressed={selected}
      disabled={cannotSelect}
      aria-label={t('donations.inventory-source.item-selection-label', {
        name: item.name,
      })}
      onClick={() => {
        if (!cannotSelect) onToggle(item);
      }}
      sx={{
        width: '100%',
        minHeight: 88,
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        border: '1px solid',
        borderColor: selected ? 'secondary.main' : 'divider',
        borderRadius: 1.25,
        backgroundColor: selected ? 'secondary.50' : 'background.paper',
        boxShadow: selected
          ? (theme) => `0 0 0 1px ${theme.palette.secondary.main}`
          : 'none',
        px: { xs: 1, sm: 1.5 },
        py: 1,
        textAlign: 'start',
        transition: (theme) =>
          theme.transitions.create(
            ['background-color', 'border-color', 'transform'],
            { duration: 120 }
          ),
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            backgroundColor: selected ? 'secondary.50' : 'action.hover',
          },
        },
        '&.Mui-focusVisible': {
          outline: '3px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
        '&:active': {
          transform: 'translateY(1px)',
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:active': { transform: 'none' },
        },
      }}
    >
      <Stack
        direction="row"
        spacing={{ xs: 1.25, sm: 1.75 }}
        alignItems="center"
        width="100%"
        minWidth={0}
      >
        {image ? (
          <Box
            component="img"
            src={getImageUrl(image)}
            alt=""
            loading="lazy"
            width={64}
            height={64}
            sx={{
              width: 64,
              height: 64,
              borderRadius: 1,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            aria-hidden="true"
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 64,
              height: 64,
              borderRadius: 1,
              backgroundColor: 'grey.100',
              color: 'text.secondary',
              flexShrink: 0,
            }}
          >
            <Inventory2Outlined />
          </Box>
        )}

        <Stack spacing={0.75} minWidth={0} flex={1}>
          <Typography
            variant="subtitle1"
            color="primary.main"
            noWrap
            sx={{ lineHeight: 1.3 }}
          >
            {item.name}
          </Typography>

          {chips.length ? (
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {chips.map((chip) => (
                <Chip
                  key={chip.key}
                  label={chip.label}
                  size="small"
                  color={chip.color || 'default'}
                  variant={chip.variant || 'outlined'}
                  sx={{
                    maxWidth: '100%',
                    borderRadius: 0.75,
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              ))}
            </Stack>
          ) : null}
        </Stack>
      </Stack>
    </ButtonBase>
  );
}

function InventoryListSkeleton() {
  return (
    <Stack spacing={1.5} aria-hidden="true">
      {[0, 1, 2].map((item) => (
        <Skeleton key={item} variant="rounded" height={88} />
      ))}
    </Stack>
  );
}

export default function InventoryDonationSourceStep({
  error,
  hasMore,
  items,
  loading,
  onRetry,
  onSearchChange,
  onToggle,
  search,
  selectedItems,
  compact = false,
  preventIncrease = false,
}) {
  const { t } = useTranslation('translation');
  const { weight: weightUnit = 'lbs' } = useLocaleUnit();
  const selectedIds = new Set(selectedItems.map((item) => item.id));

  return (
    <Stack
      spacing={compact ? 1 : 2.5}
      mt={compact ? 0 : 3}
      sx={compact ? { minHeight: 0, flex: 1, height: '100%' } : undefined}
    >
      <TextField
        fullWidth
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder={t('donations.inventory-source.search-placeholder')}
        aria-label={t('donations.inventory-source.search')}
        error={error}
        inputProps={{ maxLength: 255 }}
        InputProps={{
          endAdornment:
            loading && items.length > 0 ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            minHeight: 48,
            borderRadius: 1.25,
          },
        }}
      />

      <Typography
        role="status"
        aria-live="polite"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          p: 0,
          m: -1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {t('donations.inventory-source.result-count', {
          count: items.length,
        })}
      </Typography>

      <Box
        data-testid="inventory-results"
        sx={
          compact
            ? {
                flex: 1,
                minHeight: 0,
                overflowX: 'hidden',
                overflowY: 'auto',
              }
            : undefined
        }
      >
        {loading && items.length === 0 ? <InventoryListSkeleton /> : null}

        {error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                {t('donations.inventory-source.retry')}
              </Button>
            }
          >
            {t('donations.inventory-source.load-error')}
          </Alert>
        ) : null}

        {!loading && !error && items.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent={compact ? 'center' : undefined}
            spacing={1}
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1.25,
              boxSizing: 'border-box',
              height: compact ? '100%' : undefined,
              px: 2,
              py: 4,
              textAlign: 'center',
            }}
          >
            <Inventory2Outlined color="action" />
            <Typography variant="subtitle1">
              {t('donations.inventory-source.no-matches')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('donations.inventory-source.empty-hint')}
            </Typography>
          </Stack>
        ) : null}

        {!error && items.length > 0 ? (
          <Stack
            spacing={1.5}
            sx={{
              maxHeight: compact ? 'none' : { xs: '42dvh', sm: 360 },
              overflowY: compact ? 'visible' : 'auto',
              px: 0.25,
              py: 0.25,
            }}
          >
            {items.map((item) => (
              <InventoryItemCard
                key={item.id}
                item={item}
                selected={selectedIds.has(item.id)}
                onToggle={onToggle}
                weightUnit={weightUnit}
                preventIncrease={preventIncrease}
              />
            ))}
          </Stack>
        ) : null}
      </Box>

      {hasMore ? (
        <Typography variant="caption" color="text.secondary">
          {t('donations.inventory-source.refine-search')}
        </Typography>
      ) : null}
    </Stack>
  );
}
